import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Logout,
  Dashboard as DashboardIcon,
  History,
  Settings as SettingsIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore, User } from '../store/authStore';
import authService from '../shared/services/authService';
import LanguageSelector from '../shared/components/LanguageSelector';
import { useTranslation } from '../store/languageStore';
import { brand } from '../theme';
import { ShellContext } from '../contexts/ShellContext';
import beoLogo from '../assets/images/beo-logo.png';
import { navIcons, uiIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';
import { useModuleDrawerStore } from '../store/moduleDrawerStore';
import ModuleDrawer, { DRAWER_WIDTH } from '../components/ModuleDrawer';
import { useAppStore } from '../store/appStore';
import { useNavGuardStore } from '../store/navGuardStore';
import { useCardVisibilityStore } from '../store/cardVisibilityStore';

// ─── Navigation item definitions ────────────────────────────────────────────

type NavGroup = 'features' | 'utilities' | 'account';

export interface NavItem {
  label: string;
  path: string;
  // Bundled SVG icon key. Optional — items without a bundled asset (e.g. the
  // tenancy admin entries) supply an MUI icon via `muiIcon` instead.
  iconKey?: keyof typeof navIcons;
  // MUI icon component used when no bundled `iconKey` SVG exists.
  muiIcon?: React.ElementType;
  group: NavGroup;
  badge?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  // Admin-controlled visibility key (matches CardVisibilityMap). When set,
  // the item is hidden if the admin has disabled the corresponding card.
  // Items without a cardKey are always shown (utilities/account).
  cardKey?: string;
  // i18n key for the label. Falls back to `label` when missing.
  labelKey?: string;
  // Role-based visibility predicate. When set, the item is shown only if it
  // returns true for the current user (in addition to the cardKey check).
  visible?: (user: User | null) => boolean;
}

// What the user attempted while the nav-guard was active. Captured so
// the confirmation Dialog can dispatch the right action on Leave.
type PendingAction =
  | { kind: 'navigate'; path: string }
  | { kind: 'logout' };

export const NAV_ITEMS: NavItem[] = [
  { label: 'Document Processing', path: '/process', iconKey: 'documentProcessing', group: 'features', cardKey: 'document_processing', labelKey: 'dashboard.cards.processDocuments.title' },
  { label: 'Tampering Detection', path: '/tampering-detection', iconKey: 'tamperingDetection', group: 'features', cardKey: 'tampering_detection', labelKey: 'dashboard.cards.tamperingDetection.title' },
  { label: 'Email Processor', path: '/email-processor', iconKey: 'emailProcessor', group: 'features', cardKey: 'email_processor', labelKey: 'dashboard.cards.emailProcessor.title' },
  { label: 'Supplier Declarations', path: '/supplier-declarations', iconKey: 'supplierDeclarations', group: 'features', cardKey: 'supplier_declarations' },
  { label: 'AI Master Data', path: '/master-data', iconKey: 'businessIntelligence', group: 'features', cardKey: 'master_data' },
  { label: 'Tariff Classifier', path: '/tariff-classifier', iconKey: 'tariffClassifier', group: 'features', cardKey: 'tariff_classifier', labelKey: 'dashboard.cards.tariffClassifier.title' },
  { label: 'Export Control', path: '/export-control', iconKey: 'exportControl', group: 'features', cardKey: 'export_control',labelKey: 'dashboard.cards.exportControl.title' },
  { label: 'Document Sets', path: '/document-sets', iconKey: 'documentSets', group: 'features', cardKey: 'cross_document', labelKey: 'dashboard.cards.documentSets.title' },
  { label: 'Compliance Engine', path: '/compliance', iconKey: 'complianceEngine', group: 'features', cardKey: 'compliance_engine' },
  { label: 'Customs Audit', path: '/customs-audit', iconKey: 'customsAudit', group: 'features', cardKey: 'customs_audit' },
  { label: 'Document Classifier', path: '/document-classifier', iconKey: 'documentClassifier', group: 'features', cardKey: 'document_classifier' },
  { label: 'PDF Security Scanner', path: '/pdf-security-scanner', iconKey: 'pdfSecurityScanner', group: 'features', cardKey: 'pdf_security_scanner' },
  { label: 'NLP Rules Engine', path: '/nlp-rules', iconKey: 'nlpRulesEngine', group: 'features', cardKey: 'nlp_rules' },
  { label: 'AI Learning Module', path: '#', iconKey: 'aiLearningModule', group: 'features', comingSoon: true, disabled: true, cardKey: 'ai_learning' },
  { label: 'Business Intelligence', path: '#', iconKey: 'businessIntelligence', group: 'features', comingSoon: true, disabled: true, cardKey: 'business_intelligence' },
  { label: 'Transaction History', path: '/transactions', iconKey: 'transactionHistory', group: 'utilities', labelKey: 'navigation.transactions' },
  { label: 'My Files', path: '/files', iconKey: 'myFiles', group: 'utilities', labelKey: 'navigation.files' },
  { label: 'Profile', path: '/profile', iconKey: 'profile', group: 'account', labelKey: 'navigation.profile' },
  { label: 'Settings', path: '/settings', iconKey: 'settings', group: 'account', labelKey: 'navigation.settings' },
  {
    label: 'Tenants',
    path: '/admin/tenants',
    muiIcon: ApartmentIcon,
    group: 'account',
    labelKey: 'navigation.tenants',
    visible: (user) => user?.role === 'super_admin' || user?.is_admin === true,
  },
];

const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COLLAPSED = 52;

// ─── NavRow subcomponent ────────────────────────────────────────────────────

const NavRow: React.FC<{
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  label: string;
  pinned: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent, item: NavItem) => void;
}> = ({ item, collapsed, active, label, pinned, onClick, onContextMenu }) => {
  const row = (
    <ListItemButton
      onClick={onClick}
      onContextMenu={(e) => onContextMenu(e, item)}
      disabled={item.disabled}
      aria-current={active ? 'page' : undefined}
      sx={{
        minHeight: 44,
        mx: 1,
        my: 0.75,
        borderRadius: '8px',
        px: collapsed ? 1.5 : 2,
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 1.5,
        position: 'relative',
        backgroundColor: active ? '#FFFFFF' : 'transparent',
        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
        '&:hover': {
          backgroundColor: active ? '#FFFFFF' : 'rgba(0,0,0,0.04)',
        },
      }}
    >
      {item.iconKey ? (
        <NavIcon src={navIcons[item.iconKey]} size={24} />
      ) : item.muiIcon ? (
        <Box
          component={item.muiIcon}
          sx={{ width: 24, height: 24, color: brand.textPrimary, flexShrink: 0 }}
        />
      ) : null}
      {!collapsed && (
        <Box
          sx={{
            fontSize: 14,
            fontWeight: active ? 600 : 500,
            color: brand.textPrimary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
          }}
        >
          {label}
          {item.comingSoon && (
            <Box component="span" sx={{ ml: 1, fontSize: 11, color: brand.textMuted }}>
              (soon)
            </Box>
          )}
        </Box>
      )}
      {pinned && !collapsed && (
        <NavIcon
          src={uiIcons.pin}
          size={11}
          sx={{ position: 'absolute', top: 6, right: 8, opacity: 0.85 }}
          aria-label="pinned"
        />
      )}
    </ListItemButton>
  );

  if (!collapsed) return row;

  // MUI Tooltip cannot attach to a disabled element; wrap in a span so listeners work.
  return (
    <Tooltip title={label} placement="right">
      {item.disabled ? <span style={{ display: 'block' }}>{row}</span> : row}
    </Tooltip>
  );
};

// ─── AppShell ───────────────────────────────────────────────────────────────

const AppShell: React.FC = () => {
  const { user, logout, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, tString } = useTranslation();
  const { reason: navGuardReason, setReason: setNavGuardReason } = useNavGuardStore();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebar.collapsed') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('sidebar.collapsed', collapsed ? '1' : '0');
    } catch {
      /* ignore quota errors */
    }
  }, [collapsed]);

  const { open: drawerOpen, toggle: toggleDrawer } = useModuleDrawerStore();
  const { selectedModule } = useAppStore();
  const { cards: cardVisibility, fetchCardVisibility } = useCardVisibilityStore();

  // Refresh admin-controlled visibility once on mount (mirrors Dashboard's behavior).
  useEffect(() => {
    fetchCardVisibility();
  }, [fetchCardVisibility]);

  // Items are hidden when they declare a cardKey the admin has disabled.
  // Items without a cardKey (utilities, account, Settings) are always shown.
  const isVisible = (item: NavItem): boolean => {
    // Role-gated items (e.g. tenancy admin entries) are hidden unless the
    // predicate passes for the current user.
    if (item.visible && !item.visible(user)) return false;
    if (!item.cardKey) return true;
    return cardVisibility[item.cardKey] ?? false;
  };

  const labelFor = (item: NavItem): string => {
    if (!item.labelKey) return item.label;
    const translated = tString(item.labelKey);
    return translated && translated !== item.labelKey ? translated : item.label;
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Right-click context menu for pinning a sidebar nav item. Anchored at the
  // mouse position so it pops up exactly where the user clicked. Only one nav
  // path can be pinned per user — pinning a new item replaces the old one.
  const pinnedPath = user?.pinned_nav_path ?? null;
  const [pinMenu, setPinMenu] = useState<{ x: number; y: number; item: NavItem } | null>(null);

  const handleNavContextMenu = (e: React.MouseEvent, item: NavItem) => {
    if (item.disabled || item.path === '#') return;
    e.preventDefault();
    setPinMenu({ x: e.clientX, y: e.clientY, item });
  };

  const closePinMenu = () => setPinMenu(null);

  const handlePinAction = async (action: 'pin' | 'unpin') => {
    const target = pinMenu?.item;
    closePinMenu();
    if (!target) return;
    try {
      const nextPath = action === 'pin' ? target.path : null;
      const updated = await authService.setPinnedNavPath(nextPath);
      updateUser(updated);
      toast.success(action === 'pin'
        ? `Pinned "${labelFor(target)}" as your default page`
        : 'Pin removed');
    } catch (err) {
      console.error('Failed to update pinned nav path:', err);
      toast.error('Could not update pin. Try again.');
    }
  };
  const handleLogout = async () => {
    try {
      await logout();
    } catch {}
    navigate('/');
    handleMenuClose();
  };

  // The guard interrupts any in-app exit path from /process while
  // processing is in flight. We capture WHICH exit was attempted so the
  // confirmation Dialog can dispatch the right action on Leave.
  const guardedNavigate = (path: string) => {
    if (pathname === path) return;
    if (navGuardReason) {
      setPendingAction({ kind: 'navigate', path });
      return;
    }
    navigate(path);
  };

  const guardedLogout = () => {
    if (navGuardReason) {
      setPendingAction({ kind: 'logout' });
      return;
    }
    handleLogout();
  };

  const handleNavClick = (item: NavItem) => {
    if (item.disabled || item.path === '#') return;
    guardedNavigate(item.path);
  };

  const handleConfirmLeave = () => {
    const action = pendingAction;
    setPendingAction(null);
    setNavGuardReason(null);
    if (!action) return;
    if (action.kind === 'navigate') {
      navigate(action.path);
    } else {
      handleLogout();
    }
  };

  const handleCancelLeave = () => setPendingAction(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Yellow header band */}
      <Box
        component="header"
        sx={{
          height: 60,
          backgroundColor: brand.yellow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          borderBottom: `1px solid ${brand.borderSubtle}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={beoLogo}
          alt={tString('appShell.brandLogoAlt') || 'BEO Software'}
          width={136}
          height={65}
          onClick={() => guardedNavigate(user?.is_admin ? '/admin/dashboard' : '/process')}
          sx={{ height: 48, width: 'auto', cursor: 'pointer' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageSelector />
          <IconButton
            onClick={handleMenuOpen}
            aria-label={tString('appShell.userMenuAriaLabel') || 'User menu'}
            sx={{ p: 0.25 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: brand.orange,
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {user?.firstname?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <IconButton
            aria-label={drawerOpen
              ? (tString('moduleDrawer.closeAriaLabel') || 'Close module panel')
              : (tString('moduleDrawer.openAriaLabel') || 'Open module panel')}
            data-testid="module-drawer-trigger"
            onClick={toggleDrawer}
            aria-expanded={drawerOpen}
            aria-controls="module-drawer-panel"
          >
            <NavIcon src={uiIcons.menu} size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {user?.is_admin && (
          <MenuItem
            onClick={() => {
              guardedNavigate('/admin/dashboard');
              handleMenuClose();
            }}
          >
            <DashboardIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('navigation.dashboard')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            guardedNavigate('/profile');
            handleMenuClose();
          }}
        >
          <Person sx={{ mr: 1, fontSize: 20 }} />
          {t('navigation.profile')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            guardedNavigate('/settings');
            handleMenuClose();
          }}
        >
          <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('navigation.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            guardedNavigate('/transactions');
            handleMenuClose();
          }}
        >
          <History sx={{ mr: 1, fontSize: 20 }} />
          {t('dashboard.cards.history.title')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            guardedLogout();
            handleMenuClose();
          }}
        >
          <Logout sx={{ mr: 1, fontSize: 20 }} />
          {t('auth.logout')}
        </MenuItem>
      </Menu>

      {/* Right-click context menu on a sidebar nav item — Pin / Unpin. */}
      <Menu
        open={pinMenu !== null}
        onClose={closePinMenu}
        anchorReference="anchorPosition"
        anchorPosition={pinMenu ? { top: pinMenu.y, left: pinMenu.x } : undefined}
        MenuListProps={{
          dense: true,
          sx: {
            py: 0,
            '& .MuiMenuItem-root': { fontSize: 12, minHeight: 28, px: 1.25 },
          },
        }}
      >
        {pinMenu && pinnedPath === pinMenu.item.path ? (
          <MenuItem onClick={() => handlePinAction('unpin')}>
            <NavIcon src={uiIcons.pin} size={11} sx={{ mr: 0.75, opacity: 0.6 }} />
            Unpin
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handlePinAction('pin')}>
            <NavIcon src={uiIcons.pin} size={11} sx={{ mr: 0.75 }} />
            Pin as default
          </MenuItem>
        )}
      </Menu>

      <ShellContext.Provider value={true}>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box
            component="nav"
            sx={{
              width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED,
              transition: 'width 180ms ease',
              backgroundColor: brand.sidebarSurface,
              borderRight: `1px solid ${brand.borderSubtle}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
              {NAV_ITEMS.filter(i => i.group !== 'account' && isVisible(i)).map(item => (
                <NavRow
                  key={item.path + item.label}
                  item={item}
                  collapsed={collapsed}
                  active={pathname === item.path}
                  label={labelFor(item)}
                  pinned={pinnedPath === item.path}
                  onClick={() => handleNavClick(item)}
                  onContextMenu={handleNavContextMenu}
                />
              ))}
            </Box>

            <Box
              sx={{
                p: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <IconButton
                aria-label={collapsed
                  ? (tString('appShell.expandSidebar') || 'Expand sidebar')
                  : (tString('appShell.collapseSidebar') || 'Collapse sidebar')}
                onClick={() => setCollapsed(c => !c)}
                size="small"
                sx={{
                  color: brand.textPrimary,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                }}
              >
                <NavIcon
                  src={collapsed ? uiIcons.sidebarExpand : uiIcons.sidebarCollapse}
                  size={18}
                  sx={{ filter: 'brightness(0.45)' }}
                />
              </IconButton>
            </Box>

            <Box sx={{ borderTop: `1px solid ${brand.borderSubtle}`, pt: 0.5 }}>
              {NAV_ITEMS.filter(i => i.group === 'account' && isVisible(i)).map(item => (
                <NavRow
                  key={item.path + item.label}
                  item={item}
                  collapsed={collapsed}
                  active={pathname === item.path}
                  label={labelFor(item)}
                  pinned={pinnedPath === item.path}
                  onClick={() => handleNavClick(item)}
                  onContextMenu={handleNavContextMenu}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              marginRight: drawerOpen ? `${DRAWER_WIDTH}px` : 0,
              transition: 'margin-right 220ms ease',
            }}
          >
            <Outlet />
          </Box>
        </Box>

        <ModuleDrawer selectedModuleId={selectedModule?.id} />

        <Dialog open={!!pendingAction} onClose={handleCancelLeave} aria-labelledby="nav-guard-title">
          <DialogTitle id="nav-guard-title">
            {tString('documentProcessing.navGuard.title') || 'Leave this page?'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {(tString('documentProcessing.navGuard.body') || '{reason} If you leave now, the work will continue in the background.')
                .replace('{reason}', navGuardReason ?? '')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLeave}>
              {tString('documentProcessing.navGuard.stay') || 'Stay'}
            </Button>
            <Button onClick={handleConfirmLeave} variant="contained" color="primary" autoFocus>
              {tString('documentProcessing.navGuard.leave') || 'Leave'}
            </Button>
          </DialogActions>
        </Dialog>
      </ShellContext.Provider>
    </Box>
  );
};

export default AppShell;
